import React, {
  useEffect, useState, useMemo, useCallback
} from 'react';
import { useTranslation } from 'react-i18next';
import queryString from 'query-string';
import classnames from 'classnames';
import { useLocation } from 'react-router-dom';
import { purchaseDetailsTypes, topupFlows } from '../../../../common/config/const';
import CreditCardProcessing from '../../purchases/credit_card_processing';
import PayPalProcessing from '../../purchases/pay_pal_processing';
import {
  getPurchaseURL, applePayIsSupported, googlePayIsSupported
} from '../../../../common/purchase_details_common/purchase_details_common_utils';
import Const, { contextToChatType } from '../../../config/const';
import classes from './classes.module.scss';
import ProcessApplePay from '../../../../common/payment_options/process_apple_pay';
import GooglePay from '../../../../common/payment_options/google_pay';
import MobileProcessingStatus from '../../purchases/processing_status/mobile_processing_status';
import { getPurchaseClickSource } from '../../purchases/purchases_utils';
import { toCamelCase } from '../../../../common/config/utils';
import LoadingIndicator from '../../../../common/helpers/loading_indicator';

const getForMobileBuyBtnClassName = ({ purchaseButtonLoading, disableBuyButton, purchaseButtonTitleClassName }: { purchaseButtonLoading: boolean, disableBuyButton: boolean, purchaseButtonTitleClassName: string }) => {
  if (purchaseButtonLoading) return classnames(purchaseButtonTitleClassName,  classes.purchaseButtonLoading);
  if (disableBuyButton) {
    return classnames(purchaseButtonTitleClassName, classes.disableBuyButton);
  }
  return purchaseButtonTitleClassName;
};

export interface PurchaseButtonProps {
  clientAvailableCredit: string;
  currentPaymentSources?: { kind: string, errorText?: string, id: number, context: { hasPaypalBillingAgreement?: boolean, [key: string]: any } };
  purchaseDetails: Purchase.Details;
  updatePurchaseDetails: (params: object) => void;
  userId: number;
  purchaseAmount: number;
  onPurchaseComplete: () => void;
  forMobileBuyBtnOnClick?: (requiredCredit: number) => void;
  purchaseButtonTitle: React.ReactNode | string;
  purchaseButtonTitleClassName?: string;
  purchaseButtonLoading?: boolean;
  forMobileAppClickSource?: string;
  renderStatusPopup?: boolean;
  disabled?: boolean;
  onClickHandler?: () => void;
  onPurchaseFailed?: () => void;
  isContinue?: boolean;
  advisorId?: number;
}

function PurchaseButton({
  clientAvailableCredit, currentPaymentSources, purchaseDetails, updatePurchaseDetails, userId, onPurchaseComplete, forMobileBuyBtnOnClick, purchaseAmount, purchaseButtonTitle, purchaseButtonTitleClassName,
  purchaseButtonLoading, forMobileAppClickSource, renderStatusPopup = true, disabled, onClickHandler, onPurchaseFailed, isContinue, advisorId
}: PurchaseButtonProps) {
  const { t } = useTranslation();
  const location = useLocation();

  const {
    price, purchaseType, url, requiredCredit, fees, topupFlow
  } = purchaseDetails;

  const hasEnoughtCredit = parseFloat(clientAvailableCredit) >= price;

  const { purchaseFees } = fees;

  // For quick topup banner purchaseAmount is not passed; amount comes from purchaseDetails.requiredCredit
  const effectivePurchaseAmount = purchaseAmount ?? requiredCredit;

  useEffect(() => {
    if (userId && purchaseAmount) {
      updatePurchaseDetails({
        purchaseType: purchaseDetailsTypes.ADVISOR_BONUS,
        details: {
          price: purchaseAmount
        }
      });
    }
  }, [userId, purchaseAmount]);

  const renderBuyBtnContent = () => {
    if (purchaseButtonLoading) return <LoadingIndicator />;
    return purchaseButtonTitle;
  };

  const disableBuyButton = hasEnoughtCredit ? false : (
    !currentPaymentSources || !!currentPaymentSources?.errorText
    || !effectivePurchaseAmount
    || (currentPaymentSources?.kind === 'ap' && !applePayIsSupported())
    || (currentPaymentSources?.kind === 'gp' && !googlePayIsSupported())
    || disabled);

  const renderBuyBtn = ({ onClick }: { onClick?: (params: object) => void }) => (
    <div
      className={ getForMobileBuyBtnClassName({ purchaseButtonLoading, disableBuyButton, purchaseButtonTitleClassName }) }
      onClick={ disabled ? undefined : onClick }
      onKeyDown={ disabled ? undefined : onClick }
      data-testid="purchase-button-buy-btn"
    >
      { renderBuyBtnContent() }
    </div>
  );

  const renderBtnContent = () => {
    const requiredCreditToFixed = (requiredCredit || 0).toFixed(2);
    const purchaseURLClickSource = getPurchaseClickSource(purchaseType, location);
    const purchaseURL = getPurchaseURL({
      url, requiredCredit: requiredCreditToFixed, purchaseType, clickSource: purchaseURLClickSource, topupFlow
    });
    const isQuickTopup = topupFlow === topupFlows.quick;

    if (currentPaymentSources?.kind === 'cc' || isQuickTopup) {
      return (
        <CreditCardProcessing
          purchaseURL={ purchaseURL }
          onPurchaseComplete={ onPurchaseComplete }
          type={ purchaseType }
          purchaseAmount={ requiredCredit }
          currentPaymentOption={ currentPaymentSources }
          chatType={ Const.chatType.text }
          notShowPurchaseSuccessPoppup
          renderStatusPopup={ renderStatusPopup }
          onClickHandler={ onClickHandler }
          onPurchaseFailed={ onPurchaseFailed }
          isContinue={ isContinue }
          disabled={ disabled }
        >
          { renderBuyBtn({}) }
        </CreditCardProcessing>
      );
    }
    if (currentPaymentSources?.kind === 'pp') {
      // Default PayPal processing (with dialog window)
      return (
        <PayPalProcessing
          purchaseURL={ purchaseURL }
          onPurchaseComplete={ onPurchaseComplete }
          type={ purchaseType }
          purchaseAmount={ requiredCredit }
          currentPaymentOption={ currentPaymentSources }
          chatType={ Const.chatType.text }
          notShowPurchaseSuccessPoppup
        >
          { renderBuyBtn({}) }
        </PayPalProcessing>
      );
    }

    if (currentPaymentSources?.kind === 'ap' || currentPaymentSources?.kind === 'am') {
      return (
        <ProcessApplePay
          { ...currentPaymentSources }
          currentPaymentOption={ currentPaymentSources }
          purchaseAmount={ requiredCredit }
          purchaseURL={ purchaseURL }
          chatType={ Const.chatType.text }
          onPurchaseComplete={ onPurchaseComplete }
          type={ purchaseType }
          purchaseFees={ purchaseFees }
          notShowPurchaseSuccessPoppup
          disabled={ disableBuyButton }
          advisorId={ advisorId }
          topupFlow={ topupFlow }
          isContinue={ isContinue }
          renderStatusPopup={ renderStatusPopup }
          onClickHandler={ onClickHandler }
          onPurchaseFailed={ onPurchaseFailed }
        >
          { renderBuyBtn({}) }
        </ProcessApplePay>
      );
    }
    if (currentPaymentSources?.kind === 'gp') {
      return (
        <GooglePay
          currentPaymentOption={ currentPaymentSources }
          paymentSourceId={ currentPaymentSources.id }
          requiredCredit={ requiredCredit }
          purchaseAmount={ requiredCredit }
          purchaseURL={ purchaseURL }
          chatType={ Const.chatType.text }
          onPurchaseComplete={ onPurchaseComplete }
          type={ purchaseType }
          purchaseFees={ purchaseFees }
          notShowPurchaseSuccessPoppup
          disabled={ disableBuyButton }
          advisorId={ advisorId }
        >
          { renderBuyBtn({}) }
        </GooglePay>
      );
    }
    return renderBuyBtn({});
  };

  const { context } = toCamelCase(queryString.parse(window.location.search));

  const [processing, setProcessing] = useState({ isProcessing: false, processingResponse: null });

  const processingStatus = useMemo(() => (
    <div className={ classes.processApplePayMobileProcessingStatusHolder }>
      <div className={ classes.processApplePayMobileProcessingStatusContainer }>
        <MobileProcessingStatus
          purchaseResponce={ processing?.processingResponse }
          purchaseAmount={ requiredCredit }
          bonus={ 0 }
          isAffiliatePromoPopup={ false }
          isCreditCard
          t={ t }
          type={ purchaseType }
          currentPaymentOption={ currentPaymentSources }
          purchaseFees={ purchaseFees }
          chatType={ contextToChatType[context as keyof typeof contextToChatType] }
        />
      </div>
    </div>
  ), [processing]);

  const onProcessing = (isProcessing: boolean, processingResponse: object) => {
    setProcessing({ isProcessing, processingResponse });
  };

  const renderforMobileBuyBtnContent = () => {
    if (currentPaymentSources?.kind === 'ap' || currentPaymentSources?.kind === 'am') {
      const requiredCreditToFixed = (requiredCredit || 0).toFixed(2);

      const purchaseURL = getPurchaseURL({
        url, requiredCredit: requiredCreditToFixed, purchaseType, clickSource: forMobileAppClickSource, topupFlow
      });
      return (
        <>
          <ProcessApplePay
            { ...currentPaymentSources }
            currentPaymentOption={ currentPaymentSources }
            purchaseAmount={ requiredCredit }
            purchaseURL={ purchaseURL }
            chatType={ Const.chatType.text }
            onProcessing={ onProcessing }
            onPurchaseComplete={ forMobileBuyBtnOnClick }
            type={ purchaseType }
            purchaseFees={ purchaseFees }
            clickSource={ forMobileAppClickSource }
            notShowPurchaseSuccessPoppup
            disabled={ disableBuyButton }
            advisorId={ advisorId }
            topupFlow={ topupFlow }
            isContinue={ isContinue }
          >
            { renderBuyBtn({}) }
          </ProcessApplePay>
          {processing?.isProcessing || processing?.processingResponse ? processingStatus : null}
        </>
      );
    }

    if (currentPaymentSources?.kind === 'gp') {
      const requiredCreditToFixed = (requiredCredit || 0).toFixed(2);

      const purchaseURL = getPurchaseURL({
        url, requiredCredit: requiredCreditToFixed, purchaseType, clickSource: forMobileAppClickSource, topupFlow
      });
      return (
        <>
          <GooglePay
            currentPaymentOption={ currentPaymentSources }
            paymentSourceId={ currentPaymentSources.id }
            requiredCredit={ requiredCredit }
            purchaseAmount={ requiredCredit }
            purchaseURL={ purchaseURL }
            chatType={ Const.chatType.text }
            onProcessing={ onProcessing }
            onPurchaseComplete={ forMobileBuyBtnOnClick }
            type={ purchaseType }
            purchaseFees={ purchaseFees }
            clickSource={ forMobileAppClickSource }
            notShowPurchaseSuccessPoppup
            disabled={ disableBuyButton }
            advisorId={ advisorId }
          >
            { renderBuyBtn({}) }
          </GooglePay>
          {processing?.isProcessing || processing?.processingResponse ? processingStatus : null}
        </>
      );
    }

    return renderBuyBtn({ onClick: () => forMobileBuyBtnOnClick(requiredCredit) });
  };

  const renderPurchaseButtonButtonContent = useCallback(() => {
    if (hasEnoughtCredit) {
      return renderBuyBtn({ onClick: () => { onClickHandler?.(); onPurchaseComplete(); } });
    }

    if (forMobileBuyBtnOnClick) return renderforMobileBuyBtnContent();
    return renderBtnContent();
  }, [currentPaymentSources, purchaseDetails, purchaseButtonLoading, clientAvailableCredit, processing, purchaseButtonTitle]);

  const renderPurchaseButtonButton = () => (
    <div style={ { width: '100%' } } data-testid="purchase-button-container">
      { renderPurchaseButtonButtonContent() }
    </div>
  );

  return renderPurchaseButtonButton();
}

export default PurchaseButton;
